// -----------------------------------------------------------------------------
// A7.6 Bulk edit projects + A7.6.1 Review Changes (v0.9.0 — boards round 30 #9, round 31 #4; Kyle 30/Oct – 01/Nov/26).
// Admin console › Projects › Bulk edit (super admin, Administrators):
//   1. Export Excel — every project as a sheet: Code · Project name · Client · Project owner · Modules (core/xlsx.ts).
//   2. Upload your changes — the edited sheet is read in the browser and checked by admin_projects_bulk (0022) WITHOUT
//      saving: Review Changes lists each cell that differs (now → new), new projects (a row with a new code) and errors.
//   3. Apply saves every listed change; error rows are left out. Existing codes never change; nothing is deleted
//      (projects missing from the sheet stay as they are); members are not in the sheet (Manage members).
// -----------------------------------------------------------------------------
import { useRef, useState } from "react";
import { rpc } from "../api/backend";
import type { AdminProjects, BulkResult, BulkRow } from "../api/types";
import { projectsXlsx, readBulkSheet } from "../xlsx";
import { currentToday } from "../format";
import { ErrorBox, Modal, toast } from "../ui/kit";
import { Icon } from "../ui/icons";

const TIP = ["Export downloads every project as an Excel file: Code, Project name, Client, Project owner, Modules.",
  "Change cells, add rows for new projects, then upload the file.", "You see every change in Review Changes (A7.6.1) before anything is saved.",
  "Existing codes never change; a row with a new code creates a project.", "Members are not in the file — use Manage members."];
const REVIEW_TIP = ["Only cells that differ from BuildNexx are listed.", "Rows with an error are left out; fix them in the file and upload again.",
  "Apply saves all listed changes at once."];
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
const fileName = () => `${currentToday()} BuildNexx projects.xlsx`;

export function BulkEditModal({ data, onClose, onApplied }: { data: AdminProjects; onClose: () => void; onApplied: () => void }) {
  const [rows, setRows] = useState<BulkRow[] | null>(null);
  const [name, setName] = useState("");
  const [preview, setPreview] = useState<BulkResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const exportSheet = () => {
    const url = URL.createObjectURL(new Blob([projectsXlsx(data) as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a"); a.href = url; a.download = fileName(); a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const read = async (f: File | undefined) => {
    if (!f) return;
    setErr(null); setBusy(true);
    try {
      const r = await readBulkSheet(await f.arrayBuffer());
      if (!r.length) throw new Error("The sheet has no project rows");
      const p = await rpc("admin_projects_bulk", { rows: r });
      setRows(r); setName(f.name); setPreview(p);
    } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };
  const apply = async () => {
    if (!rows) return;
    setBusy(true); setErr(null);
    try {
      const r = await rpc("admin_projects_bulk", { rows, apply: true });
      toast(`Saved: ${r.updated} updated · ${r.new} new${r.errors ? ` · ${r.errors} left out` : ""}`);
      onApplied();
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  const picker = <input ref={input} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden
    onChange={(e) => { read(e.target.files?.[0]); e.target.value = ""; }} data-testid="bulk-file" />;

  if (preview) {
    const toApply = preview.changes + preview.new;
    return (
      <Modal title="Bulk edit projects - Review Changes" info={REVIEW_TIP} onClose={onClose} className="bulk-review">
        <div className="row gap8" style={{ marginBottom: 10 }}><button className="btn" onClick={() => input.current?.click()}>Choose another file</button>
          <span className="muted small">{name} · {rows?.length} rows</span>{picker}</div>
        <div className="bulk-scroll">
          <table className="bulk-table" data-testid="bulk-review">
            <colgroup><col style={{ width: 44 }} /><col style={{ width: 80 }} /><col style={{ width: 110 }} /><col /><col style={{ width: 80 }} /></colgroup>
            <thead><tr><th className="num">Row</th><th>Code</th><th>Field</th><th>Now <Icon name="arrow_forward" /> new</th><th>Result</th></tr></thead>
            <tbody>
              {preview.items.map((i, k) => (
                <tr key={k} className={i.kind} data-bulk-row={`${i.code}:${i.kind}`}>
                  <td className="num muted">{i.row}</td><td className="code-cell strong">{i.code}</td><td>{i.field}</td>
                  <td className="msg">{i.kind === "upd" ? <><span className="muted">{i.old || "–"}</span> <Icon name="arrow_forward" /> <b>{i.new || "–"}</b></> : i.new}</td>
                  <td><span className={`pill ${i.kind === "upd" ? "p-upd" : i.kind === "new" ? "p-new" : "p-error"}`}>{i.kind === "upd" ? "Update" : i.kind === "new" ? "New" : "Error"}</span></td>
                </tr>))}
              {preview.items.length === 0 && <tr><td colSpan={5} className="muted">Nothing differs from BuildNexx.</td></tr>}
            </tbody>
          </table>
        </div>
        {err && <ErrorBox message={err} />}
        <div className="modal-foot"><span className="muted small grow" data-testid="bulk-counts">
          {plural(preview.updated, "project")} updated ({plural(preview.changes, "change")}) · {preview.new} new · {preview.unchanged} unchanged
          {preview.errors > 0 && <> · <span className="overdue">{plural(preview.errors, "error")} left out</span></>}</span>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || toApply === 0} onClick={apply}>Apply {toApply} change{toApply === 1 ? "" : "s"}</button></div>
      </Modal>
    );
  }
  return (
    <Modal title="Bulk edit projects" info={TIP} onClose={onClose} className="bulk-modal">
      <div className="bulk-step"><span className="n">1</span><span className="t"><b>Export to Excel</b><span>All {data.projects.filter((p) => !p.is_demo).length} projects · {fileName()}</span></span>
        <button className="btn" onClick={exportSheet} data-testid="bulk-export"><Icon name="file_download" /> Export Excel</button></div>
      <div className="bulk-step"><span className="n">2</span><span className="t"><b>Upload your changes</b><span>Change cells or add rows for new projects</span></span></div>
      <div className={`drop-zone${over ? " over" : ""}`} role="button" tabIndex={0} onClick={() => input.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") input.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); read(e.dataTransfer.files[0]); }}>
        <Icon name="file_upload" /> {busy ? "Reading…" : <>Drag the .xlsx here or <span className="accent-text">Browse…</span></>}</div>
      {picker}
      <p className="muted small">Codes of existing projects don't change · a new code creates a project · nothing is saved until you review and Apply.</p>
      {err && <ErrorBox message={err} />}
      <div className="modal-foot"><button className="btn" onClick={onClose}>Cancel</button></div>
    </Modal>
  );
}
